import React, { useEffect, useRef } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { Breakpoint, pluralize } from '@sketch/components'

import { ResponsiveValues } from '@sketch/global-styles'

import {
  useFlag,
  routes,
  useUserProfile,
  isProjectRoute,
  isDraftsRoute,
} from '@sketch/modules-common'

import EmptyState from '../EmptyStateOld'

import {
  DocumentGridItemPlaceholder,
  DocumentTableItemPlaceholder,
  RenderSharesDropdown,
} from '../DocumentItem'

import {
  GetStorageItemsQueryVariables,
  ShareListItemFragment,
  StorageItemFragment,
  useGetDocumentListSettingsQuery,
} from '@sketch/gql-types'

import {
  ActionTableCell,
  Footer,
  HeaderTableCell,
  FocusLink,
} from './DocumentItemsLayout.styles'
import {
  DraggableShareItem,
  DraggableShareItemProps,
} from '../DraggableShareItem'
import { useStorageItemsFilters } from '../../../storageItems/hooks'
import { useSortingSettings } from '../../hooks/useSortSettings'
import { parseFilters } from '../../../projects/hooks/useProject'
import { StorageItemsLayout } from '../../../storageItems/components'
import { useSelectionContext } from '../../../storageItems/components/SelectionContext'
import { AnimatedSelectionBottomSink } from '../../../storageItems/components/SelectionBottomSink'
import { useDropStorageItemsIntoShare } from '../../hooks/useDropStorageItemsIntoShare'
import { isTouchDevice } from '@sketch/utils'

interface CustomColumns {
  customColumns?: ResponsiveValues<number>
}

interface DocumentItemsLayoutProps<S> extends CustomColumns {
  workspaceId: string
  shares: S[]
  onLoadMore: () => Promise<any>
  totalCount: number
  renderDropdown: RenderSharesDropdown<S>
  action?: (share: S) => (() => void) | 'link'
  search?: string
  canUseDesignSystemManager?: boolean
  projectId?: string
  /** In some places, dragging is allowed but dropping is not allowed */
  canDropShares?: boolean
}

export const DocumentItemsLayout = <S extends ShareListItemFragment>(
  props: DocumentItemsLayoutProps<S>
) => {
  const {
    workspaceId,
    shares,
    onLoadMore,
    action: actionCreator = () => 'link',
    totalCount,
    renderDropdown,
    search,
    customColumns,
    projectId,
    canDropShares,
  }: typeof props = props

  const history = useHistory()

  const isDesignSystemsOn = useFlag('design-systems')

  // Route match
  const { path } = useRouteMatch()
  const isProjectView = isProjectRoute(path)
  const isDraftView = isDraftsRoute(path)
  const isValidViewToCreateProject = isProjectView || isDraftView

  const isWorkspaceLibrariesRoute = useRouteMatch({
    path: routes.WORKSPACE_LIBRARIES.template(),
    exact: true,
  })

  // Make sure the onLoadMore Reference is always the latest
  const onLoadMoreRef = useRef<() => Promise<any>>(onLoadMore)
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore
  }, [onLoadMore])

  // Load the document layout
  const { data } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = data || {}

  // Get the User Identifier to show the "You" label
  const { data: userData } = useUserProfile()
  const userIdentifier = userData?.me.identifier

  const { sort } = useSortingSettings()
  const { filters } = useStorageItemsFilters()

  // Drag and Drop
  const storageItemsQueryVariables = {
    projectIdentifier: projectId || '',
    search,
    ...parseFilters(filters, search),
    after: null,
    sharesSortOrder: sort,
  } satisfies GetStorageItemsQueryVariables

  const dropStorageItemsToNestedProject = useDropStorageItemsIntoShare({
    projectIdentifier: projectId,
    storageItemsQueryVariables,
  })

  // Selection
  const { toggleItemSelection, showSelectionCheckbox, isSelected } =
    useSelectionContext()

  const isTouch = isTouchDevice()

  const renderGridItem = (item: S, isMobile: boolean) => {
    const action = actionCreator(item)

    const userCanUpdateSettings =
      item.__typename === 'Share' && item.userCanUpdateSettings

    const commonDraggableShareItemProps: DraggableShareItemProps<S> = {
      presentation: 'grid',
      workspaceIdentifier: workspaceId,
      item,
      renderDropdown,
      draggable: !(isMobile || isTouch) && userCanUpdateSettings,
      onStorageItemsDropped: isValidViewToCreateProject
        ? draggedItems =>
            dropStorageItemsToNestedProject([...draggedItems, item])
        : undefined,
      selected: isSelected(item),
      onSelectChange(state: boolean) {
        toggleItemSelection(item, state)
      },
      showSelectionCheckbox,
      userIdentifier,
    }

    const element = showSelectionCheckbox ? (
      <DraggableShareItem
        {...commonDraggableShareItemProps}
        onClick={() => toggleItemSelection(item)}
      />
    ) : (
      <DraggableShareItem
        {...commonDraggableShareItemProps}
        onClick={action !== 'link' ? action : undefined}
      />
    )

    const shouldWrapInLink = !showSelectionCheckbox && action === 'link'

    /**
     * Sadly, we need to wrap the element with a FocusLink even if we don't have to navigate
     * because when changing to the selection mode, react will unmount the DraggableShareItem and mount it again
     * causing issues with image loader as pointed out [here](https://github.com/sketch-hq/cloud-frontend/pull/9710#discussion_r2624157807)
     */
    return (
      <FocusLink
        key={item.identifier}
        draggable={false}
        to={{
          pathname: routes.SHARE_VIEW.create({
            shareID: item.identifier,
          }),
          state: { componentsView: true },
        }}
        onClick={
          /* Prevent the link action from happening when not shouldWrapInLink */
          !shouldWrapInLink ? event => event.preventDefault() : undefined
        }
      >
        {element}
      </FocusLink>
    )
  }

  const handleListItemClick = (
    event: React.MouseEvent<HTMLElement>,
    item: StorageItemFragment
  ) => {
    const link = routes.SHARE_VIEW.create({
      shareID: item.identifier,
    })

    if (event.metaKey) {
      /* Simulate the link opening with meta key */
      window.open(link, '_blank')
    } else {
      history.push(link)
    }
  }

  const renderListItem = (item: S, isMobile: boolean) => {
    const action = actionCreator(item)

    const userCanUpdateSettings =
      item.__typename === 'Share' && item.userCanUpdateSettings

    return (
      <DraggableShareItem
        presentation="list"
        workspaceIdentifier={workspaceId}
        userIdentifier={userIdentifier}
        item={item}
        renderDropdown={renderDropdown}
        draggable={!isMobile && userCanUpdateSettings}
        droppable={canDropShares}
        onStorageItemsDropped={
          isValidViewToCreateProject
            ? draggedItems =>
                dropStorageItemsToNestedProject([...draggedItems, item])
            : undefined
        }
        onClick={event =>
          action === 'link' ? handleListItemClick(event, item) : action()
        }
        selected={isSelected(item)}
        onSelectChange={() => {
          toggleItemSelection(item)
        }}
      />
    )
  }

  const renderLayout = (
    isMobile: boolean,
    customColumns?: ResponsiveValues<number>
  ) => {
    // for design systems search: check if we're on the library route and if empty count for a special message
    if (isWorkspaceLibrariesRoute && totalCount === 0 && isDesignSystemsOn) {
      return (
        <EmptyState
          search={search}
          title="No Libraries"
          description="No Libraries added yet."
          icon="link"
        />
      )
    }

    const commonStorageItemsLayoutProps = {
      items: shares,
      onLoadMore: onLoadMore,
      customColumns: customColumns,
      totalCount: totalCount,
    }

    if (documentsLayout === 'LIST') {
      const headerLabel = pluralize('Document', 'Documents', totalCount)
      const headerColumns = [
        {
          label: `${totalCount} ${headerLabel}`,
          customCell: HeaderTableCell,
        },
        { label: 'Folder', customCell: HeaderTableCell },
        { label: 'Last Updated', customCell: HeaderTableCell },
        { label: 'Updated By', customCell: HeaderTableCell },
        { label: '', customCell: ActionTableCell },
      ]

      return (
        <StorageItemsLayout
          header={headerColumns}
          Skeleton={props => (
            <DocumentTableItemPlaceholder
              columnCount={headerColumns.length}
              {...props}
            />
          )}
          renderItem={item => renderListItem(item, isMobile)}
          {...commonStorageItemsLayoutProps}
        />
      )
    } else {
      return (
        <StorageItemsLayout
          Skeleton={DocumentGridItemPlaceholder}
          renderItem={item => renderGridItem(item, isMobile)}
          {...commonStorageItemsLayoutProps}
        />
      )
    }
  }

  return (
    <>
      <Breakpoint on="sm">
        {matches => renderLayout(!matches, customColumns)}
      </Breakpoint>

      <AnimatedSelectionBottomSink workspaceIdentifier={workspaceId} />
      <Footer />
    </>
  )
}
